'use client'

import { cva, type VariantProps } from 'class-variance-authority'
import React from 'react'

import { cn } from '../../utils/cn'
import { BadgeSize, BadgeVariant } from './types'

const badgeVariants = cva('shrink-0 items-center justify-center truncate', {
  variants: {
    variant: {
      default: 'rounded-full bg-gray-05 text-gray-02',
      men: 'rounded-full bg-blue-03 text-blue-01',
      women: 'rounded-full bg-red-03 text-red-01',
      outlined: 'rounded-md border border-solid border-gray-03 text-gray-02',
      'outlined-round':
        'rounded-full border border-solid border-gray-03 text-gray-02',
    },
    size: {
      sm: 'px-2 py-px typography-caption2',
      md: 'px-2.5 py-px typography-caption1',
    },
    fullWidth: {
      true: 'flex',
      false: 'inline-flex',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    fullWidth: false,
  },
})

const responsiveSizeClasses: Record<BadgeSize, { tablet: string; pc: string }> =
  {
    sm: {
      tablet: 'tablet:typography-caption2 tablet:py-px tablet:px-2',
      pc: 'pc:typography-caption2 pc:py-px pc:px-2',
    },
    md: {
      tablet: 'tablet:typography-caption1 tablet:py-px tablet:px-2.5',
      pc: 'pc:typography-caption1 pc:py-px pc:px-2.5',
    },
  }

export interface BadgeProps
  extends Omit<VariantProps<typeof badgeVariants>, 'variant' | 'size'> {
  /**
   * Class name
   */
  className?: string

  /**
   * badge variant
   */
  variant?: BadgeVariant

  /**
   * Size of badge
   */
  size?: BadgeSize

  /**
   * Size of badge on tablet viewport
   */
  tabletSize?: BadgeSize

  /**
   * Size of badge on pc viewport
   */
  pcSize?: BadgeSize

  /**
   * Full width badge
   */
  fullWidth?: boolean

  /**
   * Content of the badge
   */
  children?: React.ReactNode
}

/**
 * Badge component
 */
export const Badge = ({
  className,
  variant = 'default',
  size = 'md',
  tabletSize,
  pcSize,
  fullWidth = false,
  children,
}: BadgeProps) => {
  return (
    <span
      className={cn(
        badgeVariants({ variant, size, fullWidth }),
        tabletSize && responsiveSizeClasses[tabletSize].tablet,
        pcSize && responsiveSizeClasses[pcSize].pc,
        className,
      )}
    >
      {children}
    </span>
  )
}
